<script lang="ts" setup>
import { ref, computed } from 'vue';
import { AkCounter } from '@ankorstore/design-system';
import { snapQuantity } from '@bc/shared/util/snap-quantity';
import Analytics from '@monolith/legacy/services/analytics';
import QuantityCounterInteractedEvent, {
  type QuantityCounterTrackingContext,
} from '@monolith/legacy/services/analytics/events/quantity-counter-interacted-event';

const props = withDefaults(defineProps<{
  modelValue: number;
  step: number;
  min?: number;
  max?: number;
  disabled?: boolean;
  trackingContext?: QuantityCounterTrackingContext;
}>(), {
  min: undefined,
  max: undefined,
  disabled: false,
  trackingContext: undefined,
});

const emit = defineEmits<{ 'update:modelValue': [value: number] }>();

const counterKey = ref(0);
const effectiveMin = computed(() => props.min ?? props.step);

let pendingButtonInteraction = false;
// Captured on focus so that erasing the field and blurring reverts to the
// value the user had before the edit, not to whatever AkCounter clamped mid-type.
let valueAtFocus = props.modelValue;

const track = (type: 'increment' | 'decrement' | 'freeform', quantity: number) => {
  if (props.trackingContext) {
    Analytics.track(new QuantityCounterInteractedEvent(props.trackingContext, type, quantity));
  }
};

const onFocus = () => {
  valueAtFocus = props.modelValue;
};

const onIncrement = (value: number) => {
  pendingButtonInteraction = true;
  const clamped = props.max !== undefined ? Math.min(value, props.max) : value;
  const result = Math.max(effectiveMin.value, clamped);
  track('increment', result);
  emit('update:modelValue', result);
};

const onDecrement = (value: number) => {
  pendingButtonInteraction = true;
  const result = Math.max(effectiveMin.value, value);
  track('decrement', result);
  emit('update:modelValue', result);
};

const onRawInput = (value: number) => {
  if (pendingButtonInteraction) {
    pendingButtonInteraction = false;
    return;
  }
  if (value !== props.modelValue) {
    emit('update:modelValue', value);
  }
};

const onTyped = (event: Event) => {
  const snapped = snapQuantity((event.target as HTMLInputElement).value, {
    unitMultiplier: props.step,
    max: props.max,
    current: valueAtFocus,
  });
  if (snapped === props.modelValue) {
    counterKey.value += 1;
    return;
  }
  track('freeform', snapped);
  emit('update:modelValue', snapped);
};
</script>

<template>
  <AkCounter
    :key="counterKey"
    :model-value="modelValue"
    :step="step"
    :min="effectiveMin"
    :max="max"
    :disabled="disabled"
    variant="rounded"
    @focusin="onFocus"
    @increment="onIncrement"
    @decrement="onDecrement"
    @change="onTyped"
    @update:model-value="onRawInput"
  />
</template>
